import { bulkSyncBridge } from '@/tauri/chronicle/bulk-sync';
import { isTauri } from '@/tauri/utils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getCurrentUser } from '@/hooks/query/useQueryUser';

export const INTERRUPTED_SESSIONS = 'interruptedSessions';

/**
 * Returns the set of session IDs that are awaiting Chronicle bulk-sync
 * recovery — `live_recording` rows still at `status = 'session_created'`,
 * matching `dedup::should_recover_live_recording` exactly. Once bulk sync
 * advances a row past `session_created`, the audio is already on the server
 * via the live chunk pipeline, so the session is no longer "interrupted
 * from the user's POV" and is omitted from the set.
 *
 * Outside the Tauri runtime (web app), the query is disabled and the set is
 * always empty — recovery is a desktop-only concept.
 */
export function useQueryInterruptedSessions() {
  const userId = getCurrentUser()?.user_ref_id ?? '';

  // Freshness is driven by explicit invalidations from
  // `useChronicleBulkSyncEvents` and `useChronicleSessionControl`, which
  // fire on every state-changing bulk-sync event. `staleTime: Infinity`
  // avoids redundant refetches on observer mount or window focus on top
  // of those invalidations.
  const query = useQuery<string[]>({
    queryKey: [INTERRUPTED_SESSIONS, userId],
    queryFn: () => bulkSyncBridge.listInterruptedSessions(userId),
    enabled: isTauri() && userId.length > 0,
    staleTime: Infinity,
  });

  const interruptedSet = useMemo(() => new Set<string>(query.data ?? []), [query.data]);

  // Desktop-only: first IPC still unresolved. Disabled queries (web, or no
  // userId) stay pending — must not treat that as "unknown" or the note-gen
  // error overlay would be suppressed forever. Error settles to empty Set.
  const isInterruptedUnknown =
    isTauri() && userId.length > 0 && !query.isSuccess && !query.isError;

  return {
    interruptedSet,
    isInterruptedUnknown,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
  };
}

/** Marks the interrupted-sessions query as stale — triggers refetch when observed. */
export const invalidateInterruptedSessions = async () => {
  await reactQueryClient.invalidateQueries({
    queryKey: [INTERRUPTED_SESSIONS],
  });
};

/**
 * Reads the ground-truth interrupted set from Rust and writes it straight into
 * the cache. Used where the caller must not reveal a stale snapshot and cannot
 * rely on a refetch landing — `invalidateQueries` resolves even when the
 * refetch fails or is paused, silently retaining the stale set. Reading Rust
 * directly also covers row outcomes the FE cannot derive from the stop result
 * (e.g. a clean stop whose device-file delete was unprovable restores the row).
 * Rejects on IPC failure so the caller can fall back.
 */
export const refreshInterruptedSessionsFromRust = async (userId: string) => {
  const ids = await bulkSyncBridge.listInterruptedSessions(userId);
  reactQueryClient.setQueryData<string[]>([INTERRUPTED_SESSIONS, userId], ids);
};

/**
 * Fallback for when the Rust read fails: writes a single membership fact the
 * caller derived from the stop outcome. No-op when the query has never been
 * fetched, so it can't seed a fresh-forever empty cache.
 */
// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- noun collision: "membership" is set membership in the Chronicle interrupted-session cache, not workspace/organization-teams data
export const setInterruptedSessionCacheMembership = ({
  userId,
  sessionId,
  interrupted,
}: {
  userId: string;
  sessionId: string;
  interrupted: boolean;
}) => {
  reactQueryClient.setQueryData<string[]>([INTERRUPTED_SESSIONS, userId], (old) => {
    if (old === undefined) return undefined;
    const rest = old.filter((id) => id !== sessionId);
    return interrupted ? [...rest, sessionId] : rest;
  });
};
